/* eslint-disable camelcase */

import React from 'react';
import {IShippingIntervalOptions, IShopifyData, ISimpleSubProduct} from '../types';
import {ISubscriptionProduct} from '../../../../../scripts/vendor/shared/subscription/types';
import Money from '../../../Money';
import getFutureDateStr from '../../../../../scripts/utils/getFutureDateStr';
import StarIcon from '../Icons/StarIcon';
import getRcDiscountedPrice from '../utils/getRcDiscountedPrice';
import SubscriptionService from '../../../../../scripts/vendor/shared/subscription/subscriptionService';
import { ProductVariant } from '../../../../util/typings';

interface SimpleSubSummaryProps {
  langStrings: Record<string, string>;
  addedProducts: ISimpleSubProduct[];
  addedTotal: number;
  shippingIntervalOptions: IShippingIntervalOptions;
  shopifyData: IShopifyData;
  subService: SubscriptionService;
}

const SimpleSubSummary: React.FunctionComponent<SimpleSubSummaryProps> = ({
  langStrings,
  addedProducts,
  addedTotal,
  shippingIntervalOptions,
  shopifyData,
  subService
}: SimpleSubSummaryProps) => {
  const { summaryContent } = shopifyData.settings;

  const handleAddToBasket = (): void => {
    const cartItems: ISubscriptionProduct[] = addedProducts.map(prod => {
      const idToBeAdded: number = prod.selectedVariantId;
      return {
        id: idToBeAdded,
        quantity: prod.numAdded,
        sellingPlan: prod.sellingPlans[shippingIntervalOptions.selectedFrequency].id,
        properties: {
          _order_count: '1'
        }
      };
    });

    subService.addSubProductsToCart(cartItems);
  };

  const nextChargeDate: string = getFutureDateStr(
    new Date(),
    parseInt(shippingIntervalOptions.selectedFrequency),
    shippingIntervalOptions.shippingIntervalType
  )

  return (
    <div className="simple-sub-summary">
      <h1 className="simple-sub-summary-title">{summaryContent.summaryPageTitle}</h1>

      {addedProducts && (
        <div className="simple-sub-summary-getting">
          <h2 className="simple-sub-summary-getting-title">{summaryContent.summaryAddedProductsTitle}</h2>
          <ul className="simple-sub-summary-products">
            {addedProducts.map(prod => {
              const discountedPrice: number = getRcDiscountedPrice(prod);
              const selectedVariant = prod.variants.find((variant: ProductVariant) => variant.id === prod.selectedVariantId);
              const hasDiscount: boolean = discountedPrice < selectedVariant.price;

              const origPriceClass: string = hasDiscount && ' simple-sub-summary-product-price--orig';
              return (
                <li key={prod.id} className="simple-sub-summary-product">
                  <img
                    className="simple-sub-summary-product-img"
                    src={prod.imageUrl}
                    alt={prod.title}
                    width={96}
                    height={96}/>
                  <div className="simple-sub-summary-product-details">
                    <h3 className="simple-sub-summary-product-title">{prod.title}</h3>
                    <p className="simple-sub-summary-product-price-wrap">
                      <span className={'simple-sub-summary-product-price' + origPriceClass}>
                        <Money amount={selectedVariant.price}/>
                      </span>
                      {hasDiscount && (
                        <span className="simple-sub-summary-product-price--discounted">
                          <Money amount={discountedPrice}/>
                        </span>
                      )}
                    </p>
                  </div>
                  <p className="simple-sub-summary-product-qty">
                    {langStrings.qty} : {prod.numAdded}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="simple-sub-summary-details">
        <h2 className="simple-sub-summary-details-title">
          {summaryContent.summaryOrderDetailsTitle}
        </h2>
        <div className="simple-sub-summary-details-body">
          <h3 className="simple-sub-summary-details-subtitle">
            {langStrings.when}
          </h3>
          <p className="simple-sub-summary-details-txt" aria-label="Selected Frequency">
            {langStrings.every}&nbsp;
            {+shippingIntervalOptions.selectedFrequency + 1}&nbsp;
            {shippingIntervalOptions.shippingIntervalType}{langStrings.intervalTypeSuffix}
          </p>
          <h3 className="simple-sub-summary-details-subtitle">
            {langStrings.nextCharge}
          </h3>
          <p className="simple-sub-summary-details-txt">
            {nextChargeDate}
          </p>
        </div>
      </div>

      <div className="simple-sub-summary-info">
        <StarIcon/>
        <p className="simple-sub-summary-info-txt">
          {summaryContent.summaryInfo}
        </p>
      </div>

      <div className="simple-sub-summary-review-wrap">
        <div className="simple-sub-summary-review">
          <img
            src={summaryContent.summaryReviewStarsUrl}
            className="simple-sub-summary-review-stars"
          />

          <h2 className="simple-sub-summary-review-title">
            {summaryContent.summaryReviewTitle}
          </h2>

          <p className="simple-sub-summary-review-txt">
            {summaryContent.summaryReviewContent}
          </p>

          <div className="simple-sub-summary-review-details">
            <p className="simple-sub-summary-review-author">
              {summaryContent.summaryReviewAuthor}
            </p>
            <div className="simple-sub-summary-review-score-cont">
              <p className="simple-sub-summary-review-score">
                {summaryContent.summaryReviewScore}
              </p>
              <img
                src={summaryContent.summaryReviewLogoUrl}
                className="simple-sub-summary-review-logo"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="simple-sub-summary-total">
        <div className="simple-sub-summary-total-header">
          <span className="simple-sub-summary-total-header-txt">
            {langStrings.totalToPay}
          </span>
          <span className="simple-sub-summary-total-header-price">
            <Money amount={addedTotal}/>
          </span>
        </div>

        <button
          className="simple-sub-summary-btn simple-sub-summary-atc btn btn--primary"
          onClick={handleAddToBasket}
        >
          {langStrings.addToBasket}
        </button>

        <a href={summaryContent.summaryCtaURL} className="simple-sub-summary-btn simple-sub-summary-continue btn btn--secondary">{summaryContent.summaryCtaText}</a>
      </div>
    </div>
  );
};

export default SimpleSubSummary;